import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TooltipPortal } from '@radix-ui/react-tooltip';
import DataGridDataTypeIcon from 'admin/src/components/ui/DataGrid/DataGridDataTypeIcon';
import { FilterOperatorMenu } from 'admin/src/components/ui/DataGrid/filters/FilterOperatorMenu';
import { FilterValuePanel } from 'admin/src/components/ui/DataGrid/filters/FilterValuePanel';
import { INLINE_VALUE_LIMIT, filterValueLabel, formatFilterValue } from 'admin/src/components/ui/DataGrid/filters/formatFilterValue';
import { type FilterOperator, hasOperatorMenu, nextBooleanValues } from 'admin/src/components/ui/DataGrid/filters/operators';
import type { ActiveFilter, FilterOption, FilterableColumn } from 'admin/src/components/ui/DataGrid/filters/types';
import type { ColumnFilterOptions } from 'admin/src/components/ui/DataGrid/filters/useColumnFilterOptions';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'shared/src/components/ui';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'shared/src/components/ui/new';
import { Separator } from 'shared/src/components/ui/new/Separator';
import { cn } from 'shared/src/utils';

const HOVERABLE = '[@media(hover:hover)and(pointer:fine)]:hover:bg-accent';
const SEGMENT = cn(
  'flex h-full items-center px-2 transition-colors active:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
  HOVERABLE,
);

type ActiveFilterChipProps = {
  filter: ActiveFilter;
  columnOptions: ColumnFilterOptions;
  onChange: (column: FilterableColumn, operator: FilterOperator, values: string[]) => void;
  onRemove: (columnId: string) => void;
};

export function ActiveFilterChip({ filter, columnOptions, onChange, onRemove }: ActiveFilterChipProps) {
  const { column, operator, values } = filter;
  const ref = useRef<HTMLDivElement>(null);
  const options = columnOptions.optionsByFieldId[column.fieldId] ?? [];
  const LegendIcon = column.colDef.context?.quickIdIcon;
  const testId = `filter-chip-${column.columnId}`;

  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);

  const orphanValues = useMemo(() => {
    if (!columnOptions.optionsReady) return false;
    if (column.dataType === 'boolean') return false;
    if (column.dataType !== 'text') return false;
    if (!values.length) return true;

    const optionValues = new Set(options.map((option) => option.value));
    return values.some((value) => !optionValues.has(value));
  }, [column.dataType, columnOptions.optionsReady, options, values]);

  const isToggle = column.dataType === 'boolean';
  const collapsedTextValues = column.dataType === 'text' && values.length > INLINE_VALUE_LIMIT;

  const valueSegment = (
    <button
      type="button"
      data-testid={`${testId}-value`}
      className={cn(SEGMENT, 'gap-1.5')}
      aria-label={isToggle ? `Toggle ${column.title} filter` : `Change ${column.title} values`}
      onClick={isToggle ? () => onChange(column, operator, nextBooleanValues(values)) : undefined}
    >
      {orphanValues ? <OrphanWarning /> : null}
      <span className="max-w-[180px] truncate">{formatFilterValue(filter, options)}</span>
    </button>
  );

  const valuePopover = (
    <Popover modal={false}>
      {collapsedTextValues ? (
        <CollapsedValuesTooltip values={values} options={options}>
          <PopoverTrigger asChild>{valueSegment}</PopoverTrigger>
        </CollapsedValuesTooltip>
      ) : (
        <PopoverTrigger asChild>{valueSegment}</PopoverTrigger>
      )}
      <PopoverContent
        withPortal
        align="start"
        className="w-auto min-w-[260px] max-w-[320px] p-0"
        onPointerDownOutside={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest('[data-radix-popper-content-wrapper]') || target.closest('[data-slot="select-content"]')) {
            event.preventDefault();
          }
        }}
        onFocusOutside={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest('[data-radix-popper-content-wrapper]') || target.closest('[data-slot="select-content"]')) {
            event.preventDefault();
          }
        }}
      >
        <FilterValuePanel
          column={column}
          operator={operator}
          values={values}
          options={options}
          numberRange={columnOptions.numberRangeByFieldId[column.fieldId]}
          dateRange={columnOptions.dateRangeByFieldId[column.fieldId]}
          optionsReady={columnOptions.optionsReady}
          showOperator={false}
          onChange={(nextOperator, nextValues) => onChange(column, nextOperator, nextValues)}
        />
      </PopoverContent>
    </Popover>
  );

  return (
    <div ref={ref} data-testid={testId} className="flex shrink-0 items-center overflow-hidden rounded-lg border bg-background text-xs">
      <span className="flex h-full items-center gap-1.5 px-2 text-nowrap font-medium [&>svg]:size-3.5">
        {LegendIcon ? <LegendIcon /> : null}
        <DataGridDataTypeIcon dataType={column.dataType} className="shrink-0 text-muted-foreground" />
        {column.title}
      </span>

      <Separator orientation="vertical" />

      {hasOperatorMenu(column.dataType) ? (
        <FilterOperatorMenu
          column={column}
          operator={operator}
          values={values}
          fallbackSecondValue={rangeMax(columnOptions, column)}
          onChange={(nextOperator, nextValues) => onChange(column, nextOperator, nextValues)}
        >
          <button type="button" data-testid={`${testId}-operator`} className={cn(SEGMENT, 'text-muted-foreground')} aria-label={`Change ${column.title} operator`}>
            {operator}
          </button>
        </FilterOperatorMenu>
      ) : (
        <span data-testid={`${testId}-operator`} className="flex h-full items-center px-2 text-muted-foreground">
          {operator}
        </span>
      )}

      <Separator orientation="vertical" />

      {isToggle ? valueSegment : valuePopover}

      <Separator orientation="vertical" />

      <button
        type="button"
        data-testid={`${testId}-remove`}
        aria-label={`Remove ${column.title} filter`}
        className={cn(SEGMENT, 'w-7 justify-center rounded-none px-0 text-muted-foreground')}
        onClick={() => onRemove(column.columnId)}
      >
        <XMarkIcon className="size-3.5" />
      </button>
    </div>
  );
}

function CollapsedValuesTooltip({ values, options, children }: { values: string[]; options: FilterOption[]; children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="bottom" align="start" className="max-h-[200px] max-w-[240px] overflow-y-auto p-2 text-xs">
            <ul className="space-y-0.5">
              {values.map((value) => (
                <li key={value} className="break-words">
                  {filterValueLabel(value, options)}
                </li>
              ))}
            </ul>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}

function OrphanWarning() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0" aria-label="Selected values are not in the current data">
            <ExclamationTriangleIcon className="size-3.5 text-amber-500" />
          </span>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>Selected values are not in the current data. Clear this filter or choose new values.</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}

function rangeMax(columnOptions: ColumnFilterOptions, column: FilterableColumn) {
  if (column.dataType === 'number') {
    const range = columnOptions.numberRangeByFieldId[column.fieldId];
    return range ? String(range.max) : undefined;
  }

  if (column.dataType === 'date') return columnOptions.dateRangeByFieldId[column.fieldId]?.max;

  return undefined;
}
